import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { CSSProperties } from 'styled-components';
import { BottomBar } from './BottomBar';
import { FixedSizeList as List } from 'react-window';
import qrCode from 'src/assets/images/qr_code.png';
import { useAnimatedPagination } from 'src/view/hooks/useAnimatedPagination';
import { AnimatedValue } from './AnimatedValue';
import { useKeyboard } from 'src/view/hooks/useKeyboard/useKeyboard';
import cx from 'classnames';
import { ForwardIcon, BackIcon, CloseIcon } from 'src/components/Icons/icons';
import { useCursorIsIdle } from 'src/view/hooks/useCursorIsIdle';
import {
    getFullName,
    getPrimaryFollowers,
    getPrimaryRole,
    isOrganizationAdministrator,
    UserRole,
} from 'src/domain/user';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import container from 'src/container';
import { LogoLink } from 'src/components/Buttons/LogoLink';
import { TextIcon } from 'src/components/Icons/TextIcon';
import { CloudAnimation } from './CloudAnimation';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

type Item = {
    id: string;
    title: string;
    description: string;
    src?: string;
    Component?: FunctionComponent<any>;
};
type SlideProps = { index: number; style?: CSSProperties; item: Item };
type SlideListProps = {
    items?: Item[];
    onComplete: () => void;
    cancelable?: boolean;
};

type TranslatedSlide = {
    id: string;
    title: string;
    description: string;
    type?: 'qr' | 'cloud';
};

const getData = (
    t: TFunction,
    organizationName = '<ORGANISASJON>',
    username = '<BRUKERNAVN>',
    role: UserRole | undefined = undefined,
    consumerName = '<BRUKER>',
) => {
    const roleKey = (() => {
        switch (role) {
            case UserRole.organization:
            case UserRole.organizationB2C:
                return 'organization';
            case UserRole.creator:
                return 'creator';
            case UserRole.nextOfKin:
                return 'nextOfKin';
            default:
                return 'default';
        }
    })();

    const slides =
        (t(`onboarding.roles.${roleKey}`, {
            returnObjects: true,
            organizationName,
            username,
            consumerName,
        }) as TranslatedSlide[]) ?? [];

    return slides.map(slide => ({
        id: slide.id,
        title: slide.title,
        description: slide.description,
        Component: slide.type === 'cloud' ? CloudAnimation : undefined,
        src: slide.type === 'qr' ? qrCode : undefined,
    }));
};

const Slide = ({ style, item }: SlideProps) => {
    const Component = item.Component;
    return (
        <div
            className="text-center text-woodyBrown object-contain"
            style={style}
        >
            <div className="">
                <div className="flex flex-col-reverse md:flex-row items-center md:items-start gap-6 md:gap-10 w-full pt-6 px-10 md:px-20">
                    <div className="flex-1 max-w-prose">
                        <h1 className="font-logo md:text-left text-lightPeachMonoChrome text-2xl pb-4  text-center">
                            {item.title}
                        </h1>
                        <p className="text-center text-lightPeach md:text-lg md:text-left">
                            {item.description}
                        </p>
                    </div>

                    {Component && (
                        <div className="relative flex-none w-24 md:w-44 aspect-square">
                            <div className="absolute w-full">
                                <Component />
                            </div>
                        </div>
                    )}
                    {item.src && (
                        <div className="relative flex-none w-24 md:w-44 aspect-square ">
                            <div className="absolute w-full p-4 bg-lightPeach rounded-xl ">
                                <img className="w-full h-full" src={item.src} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
const useOnboardingInfo = () => {
    const user = useSignedInUser();
    const primaryRole = getPrimaryRole(user);
    const role = isOrganizationAdministrator(user)
        ? UserRole.organization
        : primaryRole;
    const followers = getPrimaryFollowers(user);
    const consumerId =
        role === UserRole.nextOfKin ? followers?.consumerIds?.[0] : '';
    const [organizationName, setOrganizationName] = useState('');
    const [consumerName, setConsumerName] = useState('');
    const username = user ? getFullName(user) : '';
    useEffect(() => {
        munduClient.getOrgDisplayName().then(orgname => {
            setOrganizationName(orgname);
        });
    }, []);
    useEffect(() => {
        if (!consumerId) return;
        munduClient.getConsumer(consumerId).then(consumer => {
            setConsumerName(getFullName(consumer));
        });
    }, [consumerId]);
    return {
        organizationName: organizationName || undefined,
        username: username || undefined,
        role,
        consumerName: consumerName || undefined,
    };
};

const { munduClient } = container.cradle;
export const SlideList = ({ onComplete, cancelable }: SlideListProps) => {
    const { organizationName, username, role, consumerName } =
        useOnboardingInfo();
    const { t } = useTranslation();
    const items = useMemo(
        () => getData(t, organizationName, username, role, consumerName),
        [consumerName, organizationName, role, t, username],
    );
    const scrollX = useRef(new AnimatedValue(0)).current;

    const { ref, isScrolling, currentIndex, onNextPage, onPreviousPage } =
        useAnimatedPagination(items.length - 1);

    const props = useKeyboard({
        ArrowLeft: () => (!isScrolling ? onPreviousPage() : null),
        ArrowRight: () => (!isScrolling ? onNextPage() : null),
        Enter: () =>
            !isScrolling && currentIndex === items.length - 1
                ? onComplete()
                : null,
    });
    const { isIdle } = useCursorIsIdle(2000);

    return (
        <div className="flex flex-col w-screen h-full ">
            <div className="w-full sticky left-0 right-0  py-3 px-4 text-[32px] text-lightPeachMonoChrome flex justify-between">
                <div className="pt-4 text-[32px] text-lightPeachMonoChrome ">
                    <LogoLink />
                </div>
                {cancelable && (
                    <button
                        onClick={onComplete}
                        className={cx(
                            ' text-xs p-3 group bg-transparent transition-all rounded-lg',
                            'text-lightPeach hover:bg-lightPeach hover:text-woodyBrown',
                        )}
                    >
                        <TextIcon SVG={CloseIcon} />
                    </button>
                )}
            </div>
            <div
                {...props}
                id="slides"
                className="flex-1 bg-woodyBrown focus:outline-none"
            >
                <div
                    id="right"
                    className={cx(
                        'absolute w-1/3 h-full top-0 right-0 z-80 opacity-0 rounded-l-3xl transition-all duration-300',
                        isIdle
                            ? 'cursor-none'
                            : currentIndex !== items.length - 1 &&
                                  'hover:opacity-50 cursor-pointer',
                    )}
                    onClick={!isScrolling ? onNextPage : undefined}
                >
                    <div className="absolute transform top-1/2 -translate-y-1/2  right-4 w-1/5 aspect-square text-lightPeach ">
                        <ForwardIcon />
                    </div>
                </div>
                <div
                    className={cx(
                        'absolute w-1/3 h-full left-0 z-80  opacity-0 transition-all ',
                        isIdle
                            ? 'cursor-none opacity-0'
                            : currentIndex !== 0 &&
                                  'hover:opacity-50 cursor-pointer',
                    )}
                    onClick={!isScrolling ? onPreviousPage : undefined}
                >
                    <div className="absolute transform top-1/2 -translate-y-1/2  left-4 w-1/5 aspect-square text-lightPeach">
                        <BackIcon />
                    </div>
                </div>
                <AutoSizer
                    onResize={size => {
                        if (ref.current)
                            ref.current.scrollLeft = size.width * currentIndex;
                    }}
                >
                    {({ height, width }) => {
                        return (
                            <List
                                className="overflow-hidden"
                                outerRef={ref}
                                onScroll={({ scrollOffset }) => {
                                    scrollX.setValue(scrollOffset);
                                }}
                                width={width}
                                height={height}
                                layout="horizontal"
                                itemCount={items.length}
                                itemSize={width}
                            >
                                {({ style, index }) => {
                                    const inputRange = [
                                        (index - 0.5) * width,
                                        index * width,
                                        (index + 0.5) * width,
                                    ];

                                    const scale = scrollX.interpolate({
                                        inputRange,
                                        outputRange: [0.8, 1, 0.8],
                                    });

                                    const opacity = scrollX.interpolate({
                                        inputRange: inputRange,
                                        outputRange: [0, 1, 0],
                                    });

                                    return (
                                        <Slide
                                            style={{
                                                ...style,
                                                opacity,
                                                transform: `scale(${scale}, ${scale})`,
                                            }}
                                            item={items[index]}
                                            index={index}
                                        />
                                    );
                                }}
                            </List>
                        );
                    }}
                </AutoSizer>
            </div>
            <BottomBar
                isLight={false}
                isScrolling={isScrolling}
                numPages={items.length}
                canNext
                currentPage={currentIndex}
                onNextPage={onNextPage}
                onPreviousPage={onPreviousPage}
                onClose={onComplete}
            />
        </div>
    );
};
